import { trackEvent } from '@vp/om-newrelic-tracker';
import cartSelectors from '../state/cart/selectors';

declare global {
  interface Window {
    newrelic: any;
  }
}

/**
 *  PageAction Name for this application.
 */
const CART_UI_ERROR_TYPE = 'cart-ui-error';
const CART_UI_EVENT_TYPE = 'cart-ui-event';
/**
 * Errors being tracked
 */
const VISTACART_API_SUBTYPE = 'VISTACART_API';
const CART_LOAD_FAIL = `${VISTACART_API_SUBTYPE}.CART_LOAD_FAIL`;
const CART_ITEM_REMOVE = `${VISTACART_API_SUBTYPE}.CART_ITEM_REMOVE`;
const CART_ITEM_GROUP_REMOVE = `${VISTACART_API_SUBTYPE}.CART_ITEM_GROUP_REMOVE`;
const CART_ITEM_QTY_UPDATE = `${VISTACART_API_SUBTYPE}.CART_ITEM_QTY_UPDATE`;
const DISCOUNT_CODE_SAVE_FAIL = `${VISTACART_API_SUBTYPE}.DISCOUNT_CODE_SAVE_FAIL`;
const DISCOUNT_CODE_CLEAR_FAIL = `${VISTACART_API_SUBTYPE}.DISCOUNT_CODE_CLEAR_FAIL`;
const CHECKOUT_DISABLED = `${VISTACART_API_SUBTYPE}.PROCEED_TO_CHECKOUT_DISABLED`;
const PENDING_OPERATIONS_RELOADS_MAX = `${VISTACART_API_SUBTYPE}.PENDING_OPERATIONS_RELOADS_MAX`;
const PENDING_OPERATIONS_RELOAD = `${VISTACART_API_SUBTYPE}.PENDING_OPERATIONS_RELOAD`;

const PRICING_CONTEXT_NOT_AVAILABLE = `PRICING_CONTEXT_API.NOT_AVAILABLE`;

const SAVE_FOR_LATER_CLICKED = `CARTUI.SAVE_FOR_LATER_CLICKED`;
const SAVE_FOR_LATER_CANCELLED = `CARTUI.SAVE_FOR_LATER_CANCELLED`;
const SAVE_FOR_LATER_CONFIRMED = `CARTUI.SAVE_FOR_LATER_CONFIRMED`;

const SHIPPING_API_SUBTYPE = 'SHIPPING_API';
const SHIPPING_OPTION_FETCH_RETRY = `${SHIPPING_API_SUBTYPE}.SHIPPING_OPTION_FETCH_RETRY`;

/**
 * Format error tracking data suitable for viewing (and querying) in New Relic.
 * @param {string} errorReason queriable error type describing where the error took place.
 * @param {*} errorObj Optional error object passed from exception.
 * @param {string} shopperId shopper ID of original request.
 * @param {string} tenant Tenant of original request
 */
function createCustomErrorData(
  errorReason: string,
  shopperId: string,
  tenant: string,
  errorObj: {} = {}
) {
  return {
    errorReason,
    error: JSON.stringify(errorObj),
    shopper_id: shopperId,
    tenant,
  };
}

/**
 *  * New Relic NoticeError API. Accepts a shopper ID to append on the custom attributes of this noticeError event type.
 * https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/notice-error
 * @param {*} param0 An object containing the: error object, shopperId, tenant.
 */
export function noticeError({
  error,
  shopperId = '',
  tenant = '',
}: {
  error: any;
  shopperId?: string;
  tenant?: string;
}) {
  if (window && window.newrelic && window.newrelic.noticeError) {
    window.newrelic.noticeError(error, { shopperId, tenant });
  } else {
    console.warn(
      '[NR] New Relic API not available on window, unable to notice error.',
      error
    );
  }
}

export function trackCheckoutBlockedForCustomerCart(cart: any) {
  const { shopperId, tenantName, invalidValidityChecks } = {
    shopperId: cartSelectors.selectShopperId(cart),
    tenantName: cartSelectors.selectTenant(cart),
    invalidValidityChecks: cartSelectors.selectInvalidValidityChecks(cart),
  };
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(CHECKOUT_DISABLED, shopperId, tenantName, {
      invalidValidityChecks,
    }),
  });
}

export function trackCartLoadError(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(CART_LOAD_FAIL, shopperId, tenantName, errorData),
  });
}

export function trackDiscountCodeSaveError(
  errorData: {},
  shopperId: string,
  tenantName: string,
  newCoupon: string,
  couponErrorMessageKey?: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    newCoupon,
    couponErrorMessageKey,
    ...createCustomErrorData(
      DISCOUNT_CODE_SAVE_FAIL,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackDiscountCodeClearError(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      DISCOUNT_CODE_CLEAR_FAIL,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackCartItemRemoveError(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      CART_ITEM_REMOVE,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackCartItemGroupRemoveError(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      CART_ITEM_GROUP_REMOVE,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackCartItemQuantityUpdateError(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      CART_ITEM_QTY_UPDATE,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackPricingContextNotAvailable(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      PRICING_CONTEXT_NOT_AVAILABLE,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackPendingOperationsDrivenCartReloadAttempts(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_EVENT_TYPE,
    ...createCustomErrorData(
      PENDING_OPERATIONS_RELOAD,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackMaxPendingOperationsDrivenCartReloadAttempts(
  errorData: {},
  shopperId: string,
  tenantName: string
) {
  trackEvent({
    eventName: CART_UI_ERROR_TYPE,
    ...createCustomErrorData(
      PENDING_OPERATIONS_RELOADS_MAX,
      shopperId,
      tenantName,
      errorData
    ),
  });
}

export function trackSaveForLaterClicks(
  shopperId: string,
  tenant: string,
  cartItemId: string
) {
  trackEvent({
    eventName: SAVE_FOR_LATER_CONFIRMED,
    ...{
      shopperId,
      tenant,
      cartItemId,
    },
  });
}

export function trackOpenSaveForLaterClicks(
  shopperId: string,
  tenant: string,
  cartItemId: string
) {
  trackEvent({
    eventName: SAVE_FOR_LATER_CLICKED,
    ...{
      shopperId,
      tenant,
      cartItemId,
    },
  });
}

export function trackCancelSaveForLaterClicks(
  shopperId: string,
  tenant: string,
  cartItemId: string
) {
  trackEvent({
    eventName: SAVE_FOR_LATER_CANCELLED,
    ...{
      shopperId,
      tenant,
      cartItemId,
    },
  });
}

export function trackCustomerRetryShippingFetchAfterFail(
  shopperId: string,
  tenant: string,
  siteName: string
) {
  trackEvent({
    eventName: CART_UI_EVENT_TYPE,
    eventReason: SHIPPING_OPTION_FETCH_RETRY,
    shopper_id: shopperId,
    tenant,
    siteName,
  });
}
